import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useDestroyPlanTrial } from "@circle-react/hooks/planTrials/useDestroyPlanTrial";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { usePlanTrialBanner } from "@/react/components/Layout/TrialBanner/usePlanTrialBanner";
import { useCurrentPlanApi } from "@/react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import type {
  PlanSettingsEventType,
  SpecificProperties,
} from "@/react/hooks/analytics/types";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";

import { useCanDowngrade } from "../../hooks/useCanDowngrade";

interface UseManagePlanTrialModalLogicParams {
  isVisible: boolean;
}

export const useManagePlanTrialModalLogic = ({
  isVisible,
}: UseManagePlanTrialModalLogicParams) => {
  const [isCancelled, setIsCancelled] = useState(false);
  const { refetch: refetchPundit } = usePunditUserContext();
  const { refetchCurrentPlan } = useCurrentPlanApi();
  const {
    planTrialEndDateFormatted,
    planTrialDaysRemainingInWords,
    planTrialDaysLeft,
    planTrial,
  } = usePlanTrialBanner();
  const { currentPlan } = useCurrentPlanApi();
  const { trackEvent } = useEventsTracker();

  const planId = currentPlan?.id;
  const { data: canDowngradeData, isPending: isCheckingDowngrade } =
    useCanDowngrade(planId || 0);

  const baseEventProps = useMemo<SpecificProperties>(
    () => ({
      days_left: planTrialDaysLeft,
      trial_type: planTrial?.trial_type,
      plan_name: currentPlan?.display_name,
    }),
    [planTrialDaysLeft, planTrial?.trial_type, currentPlan?.display_name],
  );

  const trackModalEvent = useCallback(
    (
      eventName: PlanSettingsEventType,
      additionalProps: SpecificProperties = {},
    ) => {
      const eventProps: SpecificProperties = {
        ...baseEventProps,
        ...additionalProps,
      };
      trackEvent(eventName, eventProps);
    },
    [baseEventProps, trackEvent],
  );

  useEffect(() => {
    if (isVisible && !isCheckingDowngrade && !isCancelled) {
      trackModalEvent("manage_plan_trial_modal.opened", {
        cannot_downgrade: canDowngradeData
          ? !canDowngradeData.can_downgrade
          : false,
      });
    }
  }, [
    isVisible,
    isCheckingDowngrade,
    canDowngradeData,
    isCancelled,
    trackModalEvent,
  ]);

  const hasFiredCancelSuccessful = useRef(false);

  useEffect(() => {
    if (isCancelled && isVisible && !hasFiredCancelSuccessful.current) {
      trackModalEvent("manage_plan_trial_modal.cancel_successful");
      hasFiredCancelSuccessful.current = true;
    }
  }, [isCancelled, isVisible, trackModalEvent]);

  const { mutateAsync: destroyPlanTrial, isPending: isLoading } =
    useDestroyPlanTrial({
      onSuccess: async () => {
        setIsCancelled(true);
        await refetchPundit();
        await refetchCurrentPlan();
      },
      onError: error => {
        trackModalEvent("manage_plan_trial_modal.cancel_error", {
          error_message: error?.message || "Unknown error",
        });
      },
    });

  const hasBlockers = canDowngradeData && !canDowngradeData.can_downgrade;

  return {
    isCancelled,
    isLoading,
    isCheckingDowngrade,
    hasBlockers,
    canDowngradeData,
    currentPlan,
    planTrialEndDateFormatted,
    planTrialDaysRemainingInWords,
    trackModalEvent,
    destroyPlanTrial,
  };
};
